import React, { useEffect, useMemo, useState } from "react";

const STATUS = {
  NOT_CONFIRMED: "NOT_CONFIRMED",
  CONFIRMED: "CONFIRMED",
  CHANGED_AFTER_CONFIRMATION: "CHANGED_AFTER_CONFIRMATION",
};

const ALLOWED_GRADE_OPTIONS = ["1", "1.5", "2", "2.5", "3", "3.5", "4", "4.5", "5", "5.5", "6"];

const formatDate = (isoDate) => {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("de-CH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const toFixedOrDash = (value, digits = 2) => {
  if (value == null || value === "") return "-";
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return "-";
  return parsed.toFixed(digits);
};

const SemesterConfirmationCard = ({
  confirmation,
  semesterSubjects,
  selectedSemester,
  selectedClass,
  canEditDefinitiveGrades = false,
  isLearner = false,
  onSaveDefinitiveGrades,
  onConfirm,
  onUploadReport,
  loading,
  debugInfo,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [localError, setLocalError] = useState("");
  const [draftDefinitive, setDraftDefinitive] = useState({});
  const [reportFiles, setReportFiles] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const status = confirmation?.status || STATUS.NOT_CONFIRMED;
  const revisorStatus = confirmation?.revisorReviewStatus;
  const resubmittedAfterRejection = !!confirmation?.resubmittedAfterRejection;
  const rejectedNeedsCorrection =
    revisorStatus === "REJECTED" && !resubmittedAfterRejection;
  const effectiveCanEditDefinitiveGrades = canEditDefinitiveGrades && (!isLearner || status === STATUS.NOT_CONFIRMED || rejectedNeedsCorrection);
  const renderMode = effectiveCanEditDefinitiveGrades ? "editable-select" : "read-only-text";

  const normalizedRows = useMemo(() => {
    const sourceRows = Array.isArray(semesterSubjects) && semesterSubjects.length > 0
      ? semesterSubjects
      : (confirmation?.subjects || []);

    return sourceRows.map((row) => {
      const calculated = row.calculatedGrade ?? row.grade ?? null;
      const definitive = row.definitiveGrade ?? null;
      const suggested = row.suggestedDefinitiveGrade ?? null;
      const effective = row.effectiveGrade ?? definitive ?? suggested ?? calculated ?? null;
      const adjusted = typeof row.adjusted === "boolean"
        ? row.adjusted
        : (definitive != null && calculated != null && Number(definitive) !== Number(calculated));

      // IT and Mediamatik modules are commonly known by their code, so it is
      // prefixed to the name - but only when the subject actually has one.
      const subjectCode = (row.subjectCode || "").trim();

      return {
        subjectId: row.subjectId,
        subjectName: row.subjectName,
        subjectLabel: subjectCode
          ? `${subjectCode} - ${row.subjectName}`
          : row.subjectName,
        calculatedGrade: calculated,
        definitiveGrade: definitive,
        suggestedDefinitiveGrade: suggested,
        effectiveGrade: effective,
        adjusted,
      };
    });
  }, [semesterSubjects, confirmation?.subjects]);

  useEffect(() => {
    const next = {};
    normalizedRows.forEach((row) => {
      const baseline = row.definitiveGrade ?? row.suggestedDefinitiveGrade;
      const value = baseline != null ? String(Number(baseline)) : "";
      next[row.subjectId] = value;
    });
    setDraftDefinitive(next);
  }, [normalizedRows]);

  useEffect(() => {
    // we don't want to expand it by default automatically
  }, [status]);

  useEffect(() => {
  }, [status, effectiveCanEditDefinitiveGrades, renderMode]);

  // A rejected semester can also be rejected because of the PDF, so the upload stays open
  // there even though a report is already attached.
  const isReportReplacement = rejectedNeedsCorrection && !!confirmation?.reportUploaded;

  const showSigningArea =
    status === STATUS.NOT_CONFIRMED || status === STATUS.CHANGED_AFTER_CONFIRMATION || rejectedNeedsCorrection;

  const statusConfig = useMemo(() => {
    if (resubmittedAfterRejection) {
      return {
        badge: "bg-amber-100 text-amber-800 border-amber-200",
        banner: "bg-amber-50 border-amber-200 text-amber-900",
        label: "Submitted for re-check",
        icon: "fas fa-redo",
      };
    }

    if (rejectedNeedsCorrection) {
      return {
        badge: "bg-red-100 text-red-700 border-red-200",
        banner: "bg-red-50 border-red-200 text-red-800",
        label: "Rejected by Revisor",
        icon: "fas fa-undo",
      };
    }

    if (status === STATUS.CONFIRMED) {
      return {
        badge: "bg-emerald-100 text-emerald-700 border-emerald-200",
        banner: "bg-emerald-50 border-emerald-200 text-emerald-800",
        label: `Confirmed on ${formatDate(confirmation?.confirmedAt)}`,
        icon: "fas fa-check-circle",
      };
    }

    if (status === STATUS.CHANGED_AFTER_CONFIRMATION) {
      return {
        badge: "bg-amber-100 text-amber-700 border-amber-200",
        banner: "bg-amber-50 border-amber-200 text-amber-900",
        label: "Changed after confirmation",
        icon: "fas fa-exclamation-triangle",
      };
    }

    return {
      badge: "bg-slate-100 text-slate-700 border-slate-200",
      banner: "bg-slate-50 border-slate-200 text-slate-800",
      label: "Not confirmed",
      icon: "fas fa-pen-nib",
    };
  }, [status, confirmation?.confirmedAt, rejectedNeedsCorrection, resubmittedAfterRejection]);

  // The learner flow is a strict sequence: save every definitive grade, then confirm,
  // then upload the Zeugnis. None of the three is optional - the semester only reaches
  // the Revisor once all of them are done.
  const gradesSaved =
    normalizedRows.length > 0 &&
    normalizedRows.every((row) => row.definitiveGrade != null);
  const gradesConfirmed =
    status === STATUS.CONFIRMED || status === STATUS.CHANGED_AFTER_CONFIRMATION;
  const reportPresent = !!confirmation?.reportUploaded;

  // The learner confirmation and the Revisor review are two separate steps. The card
  // shows both so a learner can see whether their semester was already checked.
  const reviewConfig = useMemo(() => {
    if (status !== STATUS.CONFIRMED && !revisorStatus) return null;

    // CHECKED is the single positive outcome; FINALIZED is the retired legacy value.
    if (revisorStatus === "CHECKED" || revisorStatus === "FINALIZED") {
      const checkedOn = confirmation?.reviewedAt || confirmation?.finalizedAt;
      return {
        badge: "bg-emerald-100 text-emerald-800 border-emerald-300",
        icon: "fas fa-check-double",
        label: "Checked by Revisor",
        detail: `Checked by the Revisor${checkedOn ? ` on ${formatDate(checkedOn)}` : ""}.`,
      };
    }

    if (revisorStatus === "REJECTED") {
      return {
        badge: "bg-red-100 text-red-700 border-red-300",
        icon: "fas fa-undo",
        label: "Sent back by Revisor",
        detail: resubmittedAfterRejection
          ? "Was sent back once and is now waiting for the re-check."
          : "Sent back for correction. Not checked yet.",
      };
    }

    if (status === STATUS.CONFIRMED) {
      if (!reportPresent) {
        return {
          badge: "bg-amber-100 text-amber-800 border-amber-300",
          icon: "fas fa-triangle-exclamation",
          label: "Report required",
          detail:
            "Not sent to the Revisor yet - your Report / Zeugnis is still missing.",
        };
      }

      return {
        badge: "bg-slate-100 text-slate-600 border-slate-300",
        icon: "fas fa-hourglass-half",
        label: "Awaiting Revisor check",
        detail: "Not checked yet - waiting for the Revisor.",
      };
    }

    return null;
  }, [
    status,
    revisorStatus,
    resubmittedAfterRejection,
    reportPresent,
    confirmation?.finalizedAt,
    confirmation?.reviewedAt,
  ]);

  // The two statuses can disagree (e.g. grades edited after an already finalized check),
  // so each one is spelled out separately instead of merged into a single sentence.
  const confirmationLine = useMemo(() => {
    if (status === STATUS.NOT_CONFIRMED) {
      return "Not confirmed yet.";
    }

    const signedOn = confirmation?.confirmedAt
      ? ` on ${formatDate(confirmation.confirmedAt)}`
      : "";
    const signedBy = confirmation?.signedName || confirmation?.learnerName;

    if (status === STATUS.CHANGED_AFTER_CONFIRMATION) {
      return `Confirmed${signedOn}, but the definitive grades were changed afterwards - this confirmation is out of date.`;
    }

    return `Confirmed${signedOn}${signedBy ? ` by ${signedBy}` : ""}.`;
  }, [status, confirmation?.confirmedAt, confirmation?.signedName, confirmation?.learnerName]);

  // What the learner has to do right now - never contradicting the two status lines above.
  const actionLine = useMemo(() => {
    if (rejectedNeedsCorrection) {
      return "What to do: correct the definitive grades to match your Report/Zeugnis, upload the document and confirm again.";
    }

    if (resubmittedAfterRejection) {
      return "What to do: nothing - your correction was submitted and the Revisor will check it again.";
    }

    if (status === STATUS.CHANGED_AFTER_CONFIRMATION) {
      if (revisorStatus === "CHECKED" || revisorStatus === "FINALIZED") {
        return "What to do: the Revisor check above refers to the grades as they were at that time. Verify the current grades against your Report/Zeugnis and confirm again - this sends the semester back for a new check.";
      }
      return "What to do: verify the current definitive grades against your Report/Zeugnis and confirm them again.";
    }

    if (status === STATUS.CONFIRMED) {
      if (revisorStatus === "CHECKED" || revisorStatus === "FINALIZED") {
        return "What to do: nothing - this semester is complete.";
      }
      return reportPresent
        ? "What to do: nothing - wait for the Revisor check."
        : "What to do: upload your Report / Zeugnis in step 3. Until then this semester is not sent to the Revisor.";
    }

    return gradesSaved
      ? "What to do: confirm your definitive grades in step 2, then upload your Report / Zeugnis."
      : "What to do: enter a definitive grade for every subject and save them in step 1.";
  }, [
    status,
    revisorStatus,
    rejectedNeedsCorrection,
    resubmittedAfterRejection,
    reportPresent,
    gradesSaved,
  ]);

  // The report lives in the report store; older confirmations may still carry it inline
  // as a data URL in the signature field.
  const uploadedReport = useMemo(() => {
    const raw = confirmation?.signatureData;

    if (typeof raw === "string" && raw.startsWith("data:application/pdf")) {
      return { kind: "pdf", filename: confirmation?.signedName || "Report.pdf", href: raw };
    }
    if (typeof raw === "string" && raw.startsWith("data:image")) {
      return { kind: "image", filename: confirmation?.signedName || null, href: raw };
    }

    if (confirmation?.reportUploaded) {
      return {
        kind: "reference",
        filename: confirmation?.reportFilename || confirmation?.signedName || null,
        uploadedAt: confirmation?.reportUploadedAt,
        href: null,
      };
    }

    return null;
  }, [
    confirmation?.signatureData,
    confirmation?.signedName,
    confirmation?.reportUploaded,
    confirmation?.reportFilename,
    confirmation?.reportUploadedAt,
  ]);

  const showReportUpload = gradesConfirmed && (!uploadedReport || isReportReplacement);

  useEffect(() => {
    if (!showSigningArea || !isExpanded) return;
    setLocalError("");
    setReportFiles([]);
  }, [showSigningArea, selectedSemester?.id, isExpanded]);

  const hasDefinitiveChanges = useMemo(() => {
    if (!effectiveCanEditDefinitiveGrades) return false;
    return normalizedRows.some((row) => {
      const existing = row.definitiveGrade == null ? "" : String(Number(row.definitiveGrade));
      const draft = draftDefinitive[row.subjectId] ?? "";
      return existing !== draft;
    });
  }, [normalizedRows, draftDefinitive, effectiveCanEditDefinitiveGrades]);

  const handleSaveDefinitive = async () => {
    if (!onSaveDefinitiveGrades) return;

    const payload = normalizedRows.map((row) => {
      const definitiveGrade = draftDefinitive[row.subjectId];
      return {
        subjectId: row.subjectId,
        definitiveGrade,
      };
    });

    const hasMissing = payload.some((row) => !ALLOWED_GRADE_OPTIONS.includes(String(row.definitiveGrade)));
    if (hasMissing) {
      setLocalError("Please choose a valid definitive grade for every subject.");
      return;
    }

    await onSaveDefinitiveGrades(payload);
    setLocalError("");
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;
    const file = files[0];

    if (files.length > 1) {
      setLocalError("Please select only one PDF file.");
      event.target.value = "";
      return;
    }

    if (file.type !== "application/pdf") {
      setLocalError("Only PDF files are allowed.");
      event.target.value = "";
      return;
    }

    setLocalError("");
    // Keep in sync with spring.servlet.multipart.max-file-size (100MB). This is
    // the enforced cap, not the 60MB we advertise - see LearningDocumentation.
    if (file.size > 100 * 1024 * 1024) {
      setLocalError("The file is too large. Please select a file smaller than 100MB.");
      event.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setReportFiles([
        {
          name: file.name,
          fileObj: file,
          base64: e.target.result,
        },
      ]);
    };
    reader.onerror = () => {
      setLocalError("Failed to read the file.");
      event.target.value = "";
    };
    reader.readAsDataURL(file);
  };

  const removeFile = () => {
    setReportFiles([]);
    setLocalError("");
  };

  const handleConfirm = async () => {
    setShowConfirmModal(false);
    try {
      await onConfirm();
    } catch (e) {
      setLocalError("Failed to confirm grades.");
    }
  };

  const openConfirmModal = () => {
    if (normalizedRows.length === 0) {
      setLocalError("There are no definitive grades to confirm yet.");
      return;
    }
    if (!gradesSaved) {
      setLocalError(
        'Please save a definitive grade for every subject first ("Save Definitive Grades").',
      );
      return;
    }
    setShowConfirmModal(true);
  };

  // Step 2 of the flow: the Zeugnis is attached only after the grades are confirmed.
  const handleUploadReport = async () => {
    if (!onUploadReport || reportFiles.length === 0) return;
    try {
      await onUploadReport(reportFiles);
      setReportFiles([]);
      setLocalError("");
    } catch (e) {
      setLocalError("Failed to upload the report.");
    }
  };

  return (
    <div className={`card-brand-elevated mt-8 overflow-hidden mb-8 ${rejectedNeedsCorrection ? "border-2 border-sunrise-red bg-red-50/10" : ""}`}>
      {rejectedNeedsCorrection && (
        <div className="bg-red-50 border-b border-sunrise-red px-6 py-4 flex items-start">
          <i className="fas fa-exclamation-circle text-sunrise-red mt-0.5 mr-3 text-lg"></i>
          <div>
            <h3 className="text-sm font-semibold text-red-800">
              This semester was sent back by the Revisor. You must correct your definitive grades and confirm them again.
            </h3>
            {confirmation?.rejectionReason && (
              <div className="mt-2 p-3 bg-white/60 rounded-md border border-red-100">
                <p className="text-sm text-red-900 font-medium">
                  <strong>Reason from Revisor:</strong>
                </p>
                <p className="text-sm text-red-700 mt-1">
                  {confirmation.rejectionReason}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        className="w-full text-left bg-white px-6 py-5 border-b border-gray-100 hover:bg-gray-50 transition-colors"
      >
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <h3 className="text-xl font-semibold text-grey-dusk flex items-center gap-2">
              <i className="fas fa-file-signature text-sunrise-red"></i>
              {selectedSemester?.name || "Semester"} Confirmation
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {selectedClass?.name ? `${selectedClass.name} • ` : ""}
              {confirmation?.schoolYear || selectedSemester?.schoolYear || ""}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex flex-wrap items-center justify-end gap-2">
              <span className={`inline-flex items-center px-3 py-1 rounded-full border text-xs font-semibold ${statusConfig.badge}`}>
                <i className={`${statusConfig.icon} mr-2`}></i>
                {statusConfig.label}
              </span>
              {reviewConfig && (
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full border text-xs font-semibold ${reviewConfig.badge}`}
                  title={reviewConfig.detail}
                >
                  <i className={`${reviewConfig.icon} mr-2`}></i>
                  {reviewConfig.label}
                </span>
              )}
            </div>
            <i className={`fas ${isExpanded ? "fa-chevron-up" : "fa-chevron-down"} text-gray-500`}></i>
          </div>
        </div>
      </button>

      {isExpanded && (
        <div className="p-6 bg-white">
          {isLearner && !rejectedNeedsCorrection && (
            <StepTracker
              steps={[
                { label: "Save definitive grades", done: gradesSaved },
                { label: "Confirm grades", done: gradesConfirmed },
                { label: "Upload Report / Zeugnis", done: reportPresent },
              ]}
            />
          )}

          <div className={`border rounded-xl px-4 py-3 mb-5 ${statusConfig.banner}`}>
            <dl className="space-y-1.5 text-sm">
              <div className="flex flex-col sm:flex-row sm:gap-2">
                <dt className="font-semibold shrink-0 sm:w-44">Your confirmation:</dt>
                <dd>{confirmationLine}</dd>
              </div>
              <div className="flex flex-col sm:flex-row sm:gap-2">
                <dt className="font-semibold shrink-0 sm:w-44">Revisor check:</dt>
                <dd className="flex items-start gap-2">
                  {reviewConfig ? (
                    <>
                      <i className={`${reviewConfig.icon} mt-0.5`}></i>
                      <span>{reviewConfig.detail}</span>
                    </>
                  ) : (
                    <span>Starts once you confirm this semester.</span>
                  )}
                </dd>
              </div>
            </dl>

            {rejectedNeedsCorrection && confirmation?.rejectionReason && (
              <p className="mt-2 text-sm">
                <span className="font-semibold">Reason from the Revisor:</span>{" "}
                {confirmation.rejectionReason}
              </p>
            )}

            <p className="mt-3 pt-3 border-t border-current/20 text-sm font-medium">
              {actionLine}
            </p>
          </div>

          <div className="overflow-x-auto border border-gray-100 rounded-xl">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50 text-gray-600 uppercase tracking-wide text-xs">
                <tr>
                  <th className="px-4 py-3 text-left">Subject</th>
                  <th className="px-4 py-3 text-right">Calculated</th>
                  <th className="px-4 py-3 text-right">Definitive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {normalizedRows.length === 0 ? (
                  <tr>
                    <td className="px-4 py-4 text-gray-500" colSpan={3}>
                      No definitive grades available for this semester yet.
                    </td>
                  </tr>
                ) : (
                  normalizedRows.map((row) => (
                    <tr key={row.subjectId}>
                      <td className="px-4 py-3 text-gray-700">
                        <div className="flex items-center gap-2">
                          <span>{row.subjectLabel}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">{toFixedOrDash(row.calculatedGrade, 2)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-grey-dusk">
                        {effectiveCanEditDefinitiveGrades ? (
                          <select
                            value={draftDefinitive[row.subjectId] ?? ""}
                            onChange={(event) => {
                              setDraftDefinitive((prev) => ({
                                ...prev,
                                [row.subjectId]: event.target.value,
                              }));
                              setLocalError("");
                            }}
                            className="w-24 rounded-md border border-gray-300 px-2 py-1 bg-white"
                            data-testid={`definitive-grade-select-${row.subjectId}`}
                          >
                            <option value="">Select</option>
                            {ALLOWED_GRADE_OPTIONS.map((option) => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        ) : (
                          <span>{toFixedOrDash(row.effectiveGrade, 1)}</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td className="px-4 py-3 font-semibold text-gray-700">Overall average</td>
                  <td colSpan={2} className="px-4 py-3 text-right font-bold text-grey-dusk">
                    {confirmation?.overallAverage != null ? Number(confirmation.overallAverage).toFixed(2) : "-"}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {effectiveCanEditDefinitiveGrades && (
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={handleSaveDefinitive}
                disabled={!hasDefinitiveChanges || loading}
                className="px-4 py-2 rounded-md bg-slate-700 text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="fas fa-save mr-2"></i>
                Save Definitive Grades
              </button>
            </div>
          )}

          {status === STATUS.CONFIRMED && uploadedReport && (
            <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50/60 overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-emerald-200 bg-emerald-100/60">
                <i className="fas fa-paperclip text-emerald-700 text-xs"></i>
                <span className="text-xs font-semibold uppercase tracking-wide text-emerald-800">
                  Uploaded Report
                </span>
              </div>

              <div className="p-4">
                {uploadedReport.kind === "image" ? (
                  <img
                    src={uploadedReport.href}
                    alt={uploadedReport.filename || "Uploaded report"}
                    className="max-h-64 w-full object-contain bg-white border border-emerald-100 rounded-lg"
                  />
                ) : (
                  <div className="flex items-center gap-3 rounded-lg border border-emerald-100 bg-white px-4 py-3">
                    <div className="h-10 w-10 shrink-0 rounded-lg bg-red-50 text-red-500 flex items-center justify-center">
                      <i className="fas fa-file-pdf text-lg"></i>
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-grey-dusk truncate">
                        {uploadedReport.filename || "Report / Zeugnis"}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {uploadedReport.uploadedAt || confirmation?.confirmedAt
                          ? `Uploaded on ${formatDate(
                              uploadedReport.uploadedAt || confirmation.confirmedAt,
                            )}`
                          : "Uploaded with your confirmation"}
                      </p>
                    </div>

                    {uploadedReport.href ? (
                      <a
                        href={uploadedReport.href}
                        download={uploadedReport.filename || "Report.pdf"}
                        className="shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-emerald-200 bg-white text-xs font-semibold text-emerald-800 hover:bg-emerald-50 transition-colors"
                      >
                        <i className="fas fa-download"></i>
                        Download
                      </a>
                    ) : (
                      <span className="shrink-0 text-xs text-gray-500">
                        See the Reports tab
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {showSigningArea && (
            <div className="mt-6 border border-gray-200 rounded-xl p-4 bg-gray-50">
              <p className="text-sm font-semibold text-gray-800 mb-1">
                Step 2 of 3 - confirm your definitive grades
              </p>
              <p className="text-sm text-gray-700 mb-3">
                Check that every definitive grade above matches your Zeugnis. You will be asked
                for the PDF right after confirming.
              </p>

              {!gradesSaved && (
                <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  <i className="fas fa-lock mr-2"></i>
                  Finish step 1 first: choose a definitive grade for every subject and press
                  "Save Definitive Grades".
                </div>
              )}

              {(localError || loading) && (
                <div className="mt-3 text-sm">
                  {localError && <p className="text-red-600">{localError}</p>}
                  {loading && <p className="text-gray-600">Saving...</p>}
                </div>
              )}

              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={openConfirmModal}
                  disabled={loading || normalizedRows.length === 0 || !gradesSaved}
                  className="px-5 py-2 rounded-md bg-sunrise-red text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <i className="fas fa-check mr-2"></i>
                  Confirm Semester Grade
                </button>
              </div>
            </div>
          )}

          {/* Step 3: mandatory. The Zeugnis can only be attached once the grades are confirmed.
              A sent-back semester reopens this block even though a PDF is already there - the
              wrong Zeugnis is one of the reasons the Revisor rejects a semester. */}
          {showReportUpload && (
            <div className="mt-6 border border-amber-200 rounded-xl p-4 bg-amber-50">
              <p className="text-sm font-semibold text-amber-900 mb-1">
                {isReportReplacement
                  ? "Wrong Report / Zeugnis? Upload the correct PDF"
                  : "Step 3 of 3 - upload your Report / Zeugnis (required)"}
              </p>
              <p className="text-sm text-amber-800 mb-3">
                {isReportReplacement ? (
                  <>
                    Uploading a new PDF <strong>replaces</strong> the one the Revisor sent back.
                    You still have to confirm the semester again afterwards.
                  </>
                ) : (
                  <>
                    Your grades are confirmed but this semester has <strong>not</strong> been sent
                    to the Revisor yet. It is only complete once the PDF is attached.
                  </>
                )}
              </p>

              <div className="mb-4">
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={handleFileUpload}
                  aria-label="Report PDF"
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-semibold
                    file:bg-brand-primary file:text-white
                    hover:file:bg-brand-primary/90
                    disabled:opacity-50 disabled:cursor-not-allowed
                    cursor-pointer border border-gray-300 rounded-md bg-white p-1"
                />
              </div>

              {reportFiles.length > 0 && (
                <div className="mb-4 space-y-2">
                  <p className="text-sm font-semibold text-gray-700">Selected file:</p>
                  {reportFiles.map((fileObj, index) => (
                    <div key={index} className="p-3 bg-white rounded-md border border-gray-200 flex items-center justify-between">
                      <div className="flex items-center gap-3 overflow-hidden">
                        <i className="fas fa-file-pdf text-red-500 text-xl flex-shrink-0"></i>
                        <div className="overflow-hidden">
                          <p className="text-sm font-medium text-gray-700 truncate" title={fileObj.name}>{fileObj.name}</p>
                          <p className="text-xs text-gray-500">Ready</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={removeFile}
                        className="text-gray-400 hover:text-red-500 transition-colors px-2 py-1 flex-shrink-0"
                        title="Remove file"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {(localError || loading) && (
                <div className="mt-3 text-sm">
                  {localError && <p className="text-red-600">{localError}</p>}
                  {loading && <p className="text-gray-600">Uploading...</p>}
                </div>
              )}

              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleUploadReport}
                  disabled={loading || reportFiles.length === 0}
                  className="px-5 py-2 rounded-md bg-sunrise-red text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <i className="fas fa-upload mr-2"></i>
                  {isReportReplacement ? "Replace Report" : "Upload Report"}
                </button>
              </div>
            </div>
          )}

          {showConfirmModal && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
              <div className="bg-white rounded-2xl shadow-brand-xl border border-pale-dawn w-full max-w-md overflow-hidden">
                <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-exclamation-triangle text-amber-600"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">Confirm Definitive Grades</h3>
                </div>
                
                <div className="p-6">
                  <p className="text-gray-600 leading-relaxed font-medium">
                    By confirming you declare that the saved definitive grades are exactly the grades
                    in your Report/Zeugnis. After confirming you will be asked to upload the PDF.
                  </p>
                </div>
                
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3 rounded-b-xl">
                  <button
                    type="button"
                    onClick={() => setShowConfirmModal(false)}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    className="px-5 py-2 bg-sunrise-red text-white rounded-md hover:bg-red-700 font-medium transition-colors shadow-sm"
                  >
                    Confirm & Save
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// All three steps are mandatory: the semester only reaches the Revisor when every one
// of them is done, so the learner always sees where they stand.
function StepTracker({ steps }) {
  const currentIndex = steps.findIndex((step) => !step.done);

  return (
    <ol className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-5" aria-label="Confirmation steps">
      {steps.map((step, index) => {
        const isCurrent = index === currentIndex;
        const tone = step.done
          ? "border-emerald-200 bg-emerald-50 text-emerald-800"
          : isCurrent
            ? "border-amber-300 bg-amber-50 text-amber-900"
            : "border-slate-200 bg-slate-50 text-slate-400";

        return (
          <li
            key={step.label}
            className={`flex-1 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${tone}`}
          >
            <span
              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                step.done
                  ? "bg-emerald-600 text-white"
                  : isCurrent
                    ? "bg-amber-500 text-white"
                    : "bg-slate-200 text-slate-500"
              }`}
            >
              {step.done ? <i className="fas fa-check" /> : index + 1}
            </span>
            <span className="font-medium">{step.label}</span>
            {step.done && <span className="sr-only">completed</span>}
          </li>
        );
      })}
    </ol>
  );
}

export default SemesterConfirmationCard;
